import { CommonModule } from "@angular/common";
import { Component, DestroyRef, inject, Injector, OnInit } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { take } from 'rxjs';
import { FormControl, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { CONSTANTS } from "../../../../../Constants/Constants";
import { TranslatePipe } from "@ngx-translate/core";
import { CommonDataService } from "../../../../../services/common-data.service";
import { SolPropertiesService } from "../../../../../services/properties/sol-properties.service";
import { MatDialog } from "@angular/material/dialog";
import { JobDetails } from "../../../../dialogs/common/job-details/job-details.component";

@Component({
    selector: 'app-sol-properties-task-based-labor',
    templateUrl: './sol-properties-task-based-labor.component.html',
    styleUrls: ['./sol-properties-task-based-labor.component.scss'],
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatInputModule,
        MatFormFieldModule,
        MatButtonModule,
        MatCheckboxModule,
        TranslatePipe
        ],
    standalone: true
})
export class SolPropertiesTaskLaborBasedComponent implements OnInit {
    taskBasedLaborForm: FormGroup<{
        // Miscellaneous section
        relevantDesignPages: FormControl<number>;
        designPagesBuiltDesign: FormControl<number>;
        pumpsElectric: FormControl<number>;
        fireAlarm: FormControl<boolean>;
        sprinklerHeads: FormControl<number>;
        pumpsDiesel: FormControl<number>;
        numOfDifferentHazards: FormControl<number>;
        bimCoordination: FormControl<boolean>;
        
        // Additional checkboxes and inputs
        engineeringComplete: FormControl<boolean>;
        peStampsRequired: FormControl<boolean>;
        preTestExistingDevicesCheckbox: FormControl<boolean>;
        preTestExistingDevices: FormControl<number>;
        printingSubmittalAssemblyCheckbox: FormControl<boolean>;
        printingSubmittalAssembly: FormControl<number>;
        intelligibilityDesignAreasCheckbox: FormControl<boolean>;
        intelligibilityDesignAreas: FormControl<number>;
        gridLayouts: FormControl<number>;
        areaRefugeStations: FormControl<number>;
        
        // Standard Fire System Tests
        preliminaryTesters: FormControl<number>;
        acceptanceTests: FormControl<number>;
        acceptanceTesters: FormControl<number>;
        preliminaryTestersVesda: FormControl<number>;
        acceptanceTestsVesda: FormControl<number>;
        acceptanceTestersVesda: FormControl<number>;
        
        // Fire Sprinkler System Tests
        hydrostaticTests: FormControl<number>;
        hydrostaticTesters: FormControl<number>;
        firePumpAcceptanceTests: FormControl<number>;
        firePumpAcceptanceTesters: FormControl<number>;
        acceptancePerSystemTests: FormControl<number>;
        acceptancePerSystemTesters: FormControl<number>;
    }> = new FormGroup({
        // Miscellaneous section
        relevantDesignPages: new FormControl<number>(CONSTANTS.ZERO),
        designPagesBuiltDesign: new FormControl<number>(CONSTANTS.ZERO),
        pumpsElectric: new FormControl<number>(CONSTANTS.ZERO),
        fireAlarm: new FormControl<boolean>(false),
        sprinklerHeads: new FormControl<number>(CONSTANTS.ZERO),
        pumpsDiesel: new FormControl<number>(CONSTANTS.ZERO),
        numOfDifferentHazards: new FormControl<number>(CONSTANTS.ZERO),
        bimCoordination: new FormControl<boolean>(false),
        engineeringComplete: new FormControl<boolean>(false),
        peStampsRequired: new FormControl<boolean>(false),
        preTestExistingDevicesCheckbox: new FormControl<boolean>(false),
        preTestExistingDevices: new FormControl<number>(CONSTANTS.ZERO),
        printingSubmittalAssemblyCheckbox: new FormControl<boolean>(false),
        printingSubmittalAssembly: new FormControl<number>(CONSTANTS.ZERO),
        intelligibilityDesignAreasCheckbox: new FormControl<boolean>(false),
        intelligibilityDesignAreas: new FormControl<number>(CONSTANTS.ZERO),
        gridLayouts: new FormControl<number>(CONSTANTS.ZERO),
        areaRefugeStations: new FormControl<number>(CONSTANTS.ZERO),
        preliminaryTesters: new FormControl<number>(CONSTANTS.ZERO),
        acceptanceTests: new FormControl<number>(CONSTANTS.ZERO),
        acceptanceTesters: new FormControl<number>(CONSTANTS.ZERO),
        preliminaryTestersVesda: new FormControl<number>(CONSTANTS.ZERO),
        acceptanceTestsVesda: new FormControl<number>(CONSTANTS.ZERO),
        acceptanceTestersVesda: new FormControl<number>(CONSTANTS.ZERO),
        hydrostaticTests: new FormControl<number>(CONSTANTS.ZERO),
        hydrostaticTesters: new FormControl<number>(CONSTANTS.ZERO),
        firePumpAcceptanceTests: new FormControl<number>(CONSTANTS.ZERO),
        firePumpAcceptanceTesters: new FormControl<number>(CONSTANTS.ZERO),
        acceptancePerSystemTests: new FormControl<number>(CONSTANTS.ZERO),
        acceptancePerSystemTesters: new FormControl<number>(CONSTANTS.ZERO), 
    });
    
    // Sample data properties
    preliminaryTests: number = 5;
    preliminaryTestsVesda: number = 3;
    private destroyRef = inject(DestroyRef);
    
    constructor(
        private readonly commonDataService: CommonDataService,
        private readonly solPropertiesService: SolPropertiesService,
        private readonly dialog: MatDialog,
        private readonly injector: Injector
    ) { }

    ngOnInit(): void {
        this.subscribeFormValueChanges();
        this.setupReadOnlyMode();
    }

    private subscribeFormValueChanges(): void {
        this.taskBasedLaborForm.valueChanges
        .pipe(take(CONSTANTS.ONE))
        .subscribe(
            () => this.solPropertiesService.markPropertiesChanged()
        );
    }

    private setupReadOnlyMode(): void {
        this.commonDataService.readOnlyMode$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(isReadOnly => {
                if (isReadOnly) {
                    this.taskBasedLaborForm.disable({ emitEvent: false });
                } else {
                    this.taskBasedLaborForm.enable({ emitEvent: false });
                }
            });
    }
    
    openJobDetails(): void {
        if (this.taskBasedLaborForm.disabled) {
            return;
        }

        this.dialog.open(JobDetails, {
            width: '800px',
            disableClose: true,
            injector: this.injector
        });
    }
}